/**
 * Système de logging centralisé
 * 
 * En développement : affiche tous les logs dans la console
 * En production : envoie les erreurs à un service de tracking (Sentry, etc.)
 * et masque les logs pour améliorer les performances
 */

const isDevelopment = process.env.NODE_ENV === 'development';

// Configuration pour les services de tracking (à configurer si nécessaire)
const trackingEnabled = process.env.REACT_APP_ENABLE_ERROR_TRACKING === 'true';

/**
 * Logger principal
 */
export const logger = {
  /**
   * Log d'information (dev uniquement)
   */
  log: (...args) => {
    if (isDevelopment) {
      console.log('[LOG]', ...args);
    }
  },

  /**
   * Log d'avertissement (dev uniquement)
   */
  warn: (...args) => {
    if (isDevelopment) {
      console.warn('[WARN]', ...args);
    }
  },

  /**
   * Log d'erreur (toujours loggé, peut être envoyé à un service de tracking)
   */
  error: (error, context = {}) => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;

    if (isDevelopment) {
      console.error('[ERROR]', errorMessage, context);
      if (errorStack) {
        console.error('[STACK]', errorStack);
      }
    }

    // En production, envoyer à un service de tracking si configuré
    if (!isDevelopment && trackingEnabled) {
      // TODO: Intégrer Sentry ou autre service de tracking
      // Example: Sentry.captureException(error, { extra: context });
    }
  },

  /**
   * Log de debug (dev uniquement, plus verbeux)
   */
  debug: (...args) => {
    if (isDevelopment && process.env.REACT_APP_DEBUG === 'true') {
      console.debug('[DEBUG]', ...args);
    }
  },

  /**
   * Log de performance (pour mesurer les temps d'exécution)
   */
  performance: (label, duration) => {
    if (isDevelopment) {
      console.log(`[PERF] ${label}: ${duration.toFixed(2)}ms`);
    }
  },

  /**
   * Log d'événement utilisateur (pour analytics)
   */
  event: (eventName, properties = {}) => {
    if (isDevelopment) {
      console.log('[EVENT]', eventName, properties);
    }

    // En production, envoyer à Google Analytics ou autre
    if (!isDevelopment && typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, properties);
    }
  }
};

/**
 * Helper pour mesurer le temps d'exécution d'une fonction
 */
export const measurePerformance = (label, fn) => {
  if (isDevelopment) {
    const start = performance.now();
    const result = fn();
    const duration = performance.now() - start;
    logger.performance(label, duration);
    return result;
  }
  return fn();
};

/**
 * Helper pour mesurer le temps d'exécution d'une fonction async
 */
export const measureAsyncPerformance = async (label, fn) => {
  if (isDevelopment) {
    const start = performance.now();
    const result = await fn();
    const duration = performance.now() - start;
    logger.performance(label, duration);
    return result;
  }
  return fn();
};

export default logger;



